import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy} from '@angular/core';
import {BehaviorSubject, forkJoin, of, Subject} from 'rxjs';
import {catchError, filter, finalize, map, shareReplay, switchMap, takeUntil} from 'rxjs/operators';
import {DomSanitizer} from '@angular/platform-browser';
import {ToastrService} from 'ngx-toastr';
import {PlantPhotoService} from '../../../plant-photos/plant-photo.service';
import {PlantPhoto} from '../../../plant-photos/plant-photo';
import {AuthenticationService} from '../../../_services/authentication.service';
import {HttpErrorResponse} from '@angular/common/http';

@Component({
  selector: 'app-plant-image-gallery',
  templateUrl: './plant-image-gallery.component.html',
  styleUrls: ['./plant-image-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlantImageGalleryComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly plantId$ = new BehaviorSubject<number | null>(null);
  private readonly _rolePhotographer = 'ROLE_PHOTOGRAPHER';

  protected canUpload = false;
  private settings = {
    width: '100%'
  }

  loading$ = new BehaviorSubject<boolean>(false);
  images$ = this.plantId$.pipe(
    filter(id => {
      return id != null;
    }),
    switchMap(id => {
      this.loading$.next(true);

      return this.plantPhotoService.searchByPlant(id).pipe(
        catchError(err => {
          this.toastr.error('Kon afbeeldings-metadata niet laden');
          return of([]);
        }),
        switchMap(files => {
          if (files.length === 0) {
            return of([] as Blob[]);
          }

          return forkJoin(files.map((file: PlantPhoto) => {
            return this.plantPhotoService.getImageBlob(file.fileName).pipe(
              catchError(err => {
                this.toastr.warning(`Afbeelding ${file.fileName} kon niet worden geladen`);
                return of(new Blob());
              })
            );
          }));
        }),
        map(blobs => {
          return blobs.map(blob => {
            const url = URL.createObjectURL(blob);
            return this.sanitizer.bypassSecurityTrustUrl(url);
          });
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
    takeUntil(this.destroy$)
  );

  @Input()
  set plantId(id: number) {
    this.plantId$.next(id);
  }

  constructor(
    private plantPhotoService: PlantPhotoService,
    private sanitizer: DomSanitizer,
    private cd: ChangeDetectorRef,
    private toastr: ToastrService,
    private authService: AuthenticationService
  ) {
    this.canUpload = this.authService.hasRole(this._rolePhotographer);
  }

  async onFileSelected(event: Event) {
    if (!this.authService.hasRole(this._rolePhotographer)) {
      this.toastr.error('Je hebt geen rechten om afbeeldingen te uploaden');
      return;
    }
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    const id = this.plantId$.value;

    if (!file || !id) {
      this.toastr.warning('Geen bestand geselecteerd of plant-ID ontbreekt');
      return;
    }
    this.loading$.next(true);

    try {
      const processedFile = await this.processImageWithCanvas(file, 1920, 1920, 7 * 1024 * 1024);
      this.plantPhotoService.uploadPhoto(id, processedFile).subscribe({
        next: (response) => {
          this.toastr.success('Afbeelding succesvol geüpload');
          this.refreshImages();
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === 403) {
            this.toastr.warning('Upload niet gelukt, heb je de juiste rol wel?');
          } else {
            this.toastr.error('Verwerking of upload van afbeelding is mislukt');
          }
          this.loading$.next(false);
        },
        complete: () => {
          this.loading$.next(false);
          this.cd.markForCheck();
        }
      });
    } catch (err) {
      this.toastr.error('Verwerking of upload van afbeelding is mislukt');
      this.loading$.next(false);
    }
  }

  private processImageWithCanvas(
    file: File,
    maxWidth: number,
    maxHeight: number,
    maxBytes: number
  ): Promise<File> {
    return new Promise((outerResolve, outerReject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onerror = err => {
        console.error('FileReader error:', err);
        this.toastr.error('Lezen van afbeeldingsbestand mislukt');
        outerReject(err);
      };

      reader.onload = () => {
        img.src = reader.result as string;
      };

      img.onerror = err => {
        console.error('Image load error:', err);
        this.toastr.error('Afbeelding laden voor verwerking mislukt');
        outerReject(err);
      };

      img.onload = () => {
        let { width, height } = img;
        const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          const errorMsg = 'Kan canvascontext niet ophalen';
          console.error(errorMsg);
          this.toastr.error(errorMsg);
          return outerReject(errorMsg);
        }

        ctx.drawImage(img, 0, 0, width, height);

        const tryCompress = (): Promise<File> => {
          return new Promise((innerResolve, innerReject) => {
            let quality = 1.0;

            const tryNext = () => {
              canvas.toBlob(blob => {
                if (!blob) {
                  const msg = 'Afbeeldingsconversie mislukt (toBlob returned null)';
                  console.error(msg);
                  this.toastr.error(msg);
                  return innerReject(msg);
                }

                if (blob.size <= maxBytes || quality < 0.1) {
                  const fileName = file.name.replace(/\.[^/.]+$/, '') + '.webp';
                  innerResolve(new File([blob], fileName, { type: 'image/webp' }));
                } else {
                  quality -= 0.1;
                  tryNext();
                }
              }, 'image/webp', quality);
            };

            tryNext();
          });
        };

        tryCompress().then(outerResolve).catch(outerReject);
      };

      reader.readAsDataURL(file);
    });
  }

  trackByIndex(index: number) {
    return index;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private refreshImages() {
    const id = this.plantId$.value;
    if (id != null) {
      this.plantId$.next(null);
      this.plantId$.next(id);
    }
  }

}
